import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div data-testid="chat-message-bubble"
      className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed rise ${m.role === "user"
        ? "bg-brand text-white rounded-2xl rounded-br-md" : "bg-white border text-stone-800 rounded-2xl rounded-bl-md"}`}>
      {m.text}
    </div>
  </div>
);

const Typing = () => (
  <div className="flex gap-1 px-4 py-3 bg-white border rounded-2xl rounded-bl-md w-fit" data-testid="chat-typing-indicator">
    {[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-stone-400 typing-dot" style={{ animationDelay: `${i * 0.15}s` }} />)}
  </div>
);

export default function ChatWidget({ open, setOpen }) {
  const { config, slug } = useConfig();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [options, setOptions] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open || sessionId) return;
    (async () => {
      setBusy(true);
      try {
        const { data } = await axios.post(`${API}/sites/${slug}/chat/start`);
        setSessionId(data.session_id);
        setMessages([{ role: "assistant", text: data.reply }]);
      } catch {
        setMessages([{ role: "assistant", text: "Sorry, the assistant is unavailable right now. Please try again in a moment." }]);
      } finally {
        setBusy(false);
      }
    })();
  }, [open, sessionId, slug]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy || !sessionId) return;
    setInput("");
    setOptions([]);
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/sites/${slug}/chat/message`, { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      setOptions(data.options || []);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Our team will contact you shortly." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chat-widget-trigger"
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 pl-4 pr-5 py-3.5 rounded-full btn-brand shadow-xl shadow-black/20 font-semibold text-sm">
          <span className="relative flex">
            <MessageCircle className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-accent-brand animate-ping" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-accent-brand" />
          </span>
          {config?.chatbot_name}
        </button>
      )}
      {open && (
        <div data-testid="chat-panel"
          className="fixed z-50 inset-0 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[390px] sm:h-[600px] sm:max-h-[85vh] bg-stone-50 sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden border rise">
          <div className="bg-brand text-white px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-accent-brand text-stone-900 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold leading-tight" data-testid="chat-assistant-name">{config?.chatbot_name}</p>
              <p className="text-xs text-white/75 truncate">{config?.institute_name}</p>
            </div>
            <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-white/15 transition-colors" data-testid="chat-close-button">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => <Bubble key={i} m={m} />)}
            {busy && <Typing />}
            {!busy && options.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {options.map((o) => (
                  <button key={o} onClick={() => send(o)} data-testid="chat-option-button"
                    className="px-3 py-1.5 rounded-full border border-brand text-brand bg-white text-xs font-semibold hover:bg-brand-soft transition-colors">
                    {o}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 bg-white border-t flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your message…"
              className="flex-1 rounded-full border px-4 py-2.5 text-sm outline-none focus:ring-2 ring-brand" data-testid="chat-input-field" maxLength={1000} />
            <button type="submit" disabled={busy || !input.trim()} className="w-11 h-11 rounded-full btn-brand flex items-center justify-center disabled:opacity-50" data-testid="chat-send-button">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
